import { c as _c } from "react/compiler-runtime";
import { Filters } from "@/types/filters";
import { cn } from "@/utils/cn";
import { getNameFromCode } from "@/utils/get-name-from-code";
import { ArrowUpDown, Banknote, Bath, BedDouble, Building, Car, ChevronDown, ChevronUp, Clock, Compass, Droplets, FileText, Grid3x3, Hammer, Key, Layers, LayoutGrid, Maximize, Ruler, Search, Sofa, Sparkles, Wallet, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

// Import individual filter components
import { Button } from "@/components/rac/button";
import { BalconiesSection } from "./sections/balconies-section";
import { BathroomsSection } from "./sections/bathrooms-section";
import { BedroomsSection } from "./sections/bedrooms-section";
import { DynamicAreaSection } from "./sections/dynamic-area-section";
import { FloorsSection } from "./sections/floors-section";
import { MaxAgeSection } from "./sections/max-age-section";
import { ParkingsSection } from "./sections/parkings-section";
import { PossessionSection } from "./sections/possession-section";
import { RentalValueSection } from "./sections/rental-value-section";

// Re-export types from the main filters module
export type { Filters, PropertyType, RangeValues } from "@/types/filters";
import { PlaceAutocompleteWrapper } from "@/components/others/place-autocomplete-wrapper";
import { AmenitiesIconGrid } from "@/components/rhf/amenities-icon-grid";
import { BudgetField as BudgetNew } from "@/components/rhf/budget-field";
import { Input } from "@/components/rhf/input";
import { PropertyTypeField } from "@/components/rhf/property-type-field";
import { useUrlFilters } from "@/hooks/use-url-filters";
import facingOptions from "@/info/facings";
import furnishingOptions from "@/info/furnishing-types";
import { documentsOptions } from "@/info/selectOptions/documents";
import { waterSourcesShortOptions } from "@/info/selectOptions/waterSourcesShort";
import { spaces as spacesOptions } from "@/info/spaces";
import { constructionStatus as constructionStatusOptions } from "@/info/unit/constructionStatus";
import levelOptions from "@/info/unit/levels";
import { propertyTypeOptions } from "@/info/unit/property-types";
import { MapProvider } from "@/providers/map";
import { RangeValues } from "@/types/filters";
import formikOptions from "@/utils/formikOptions";
import { handlePropertyTypeChange, handleStatusChange, isMaxAgeDisabled, isPlotFacingDisabled, isPossessionDisabled, createFormDefaults as sharedCreateFormDefaults } from "./shared-utils";
import { useFilterHandlers } from "./use-filter-handlers";

// Selected Filters Chips Component
function SelectedFilters(t0) {
  const $ = _c(18);
  const {
    filters,
    onClearFilter
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set(["locations", "propertyType", "bedrooms", "bathroomsMin", "budget", "builtupAreaMin", "floors", "balconiesMin", "parkingsMin", "rentalValue", "facing", "furnishing", "status", "amenities", "documents", "spaces", "waterSources", "levelType", "maxAge", "possession", "plotFacing"]);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const validFilterKeys = t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[1] !== filters || $[2] !== onClearFilter) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const activeFilters = Object.entries(filters).filter(t8 => {
        const [key, value] = t8;
        if (!validFilterKeys.has(key)) {
          return false;
        }
        if (key === "bounds") {
          return false;
        }
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        if (typeof value === "object" && value !== null) {
          if ("min" in value && "max" in value) {
            const rangeValue = value as RangeValues;
            return (rangeValue?.min ?? 0) > 0 || (rangeValue?.max ?? 0) > 0;
          }
          return Object.keys(value).length > 0;
        }
        if (typeof value === "string") {
          return value;
        }
        if (typeof value === "number") {
          return value > 0;
        }
        return false;
      });
      if (activeFilters.length === 0) {
        t7 = null;
        break bb0;
      }
      const optionsMap = {
        spaces: spacesOptions,
        facing: facingOptions,
        levelType: levelOptions,
        plotFacing: facingOptions,
        documents: documentsOptions,
        furnishing: furnishingOptions,
        waterSources: waterSourcesShortOptions,
        status: constructionStatusOptions.map(_temp),
        propertyType: propertyTypeOptions.map(_temp2)
      };
      t4 = "p-4 border-b bg-primary-50/40";
      t5 = "selected-filters";
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <h3 className="text-base font-bold text-primary-700 mb-2">Selected Filters</h3>;
        $[9] = t6;
      } else {
        t6 = $[9];
      }
      t2 = "flex flex-wrap gap-2";
      t3 = activeFilters.map(t9 => {
        const [key_0, value_0] = t9;
        const labelKey = key_0.charAt(0).toUpperCase() + key_0.slice(1).replace(/([A-Z])/g, " $1");
        let label;
        const options = optionsMap[key_0] || [];
        if (Array.isArray(value_0)) {
          const names = value_0.map(v => {
            if (typeof v === "string") {
              return options.length ? getNameFromCode(options, v) || v : v;
            }
            if (typeof v === "object" && v && "name" in v && typeof v.name === "string") {
              return v.name;
            }
            return "";
          }).filter(Boolean);
          const uniqueNames = Array.from(new Set(names));
          label = `${labelKey}: ${uniqueNames.join(", ")}`;
        } else {
          if (typeof value_0 === "object" && value_0 !== null && "min" in value_0 && "max" in value_0) {
            if (key_0 === "budget") {
              const formatBudgetValue = _temp3;
              label = `${labelKey}: ${formatBudgetValue(value_0.min ?? 0)} - ${formatBudgetValue(value_0.max ?? 0)} L`;
            } else {
              label = `${labelKey}: ${value_0.min} - ${value_0.max}`;
            }
          } else {
            if (typeof value_0 === "string") {
              if (!value_0) {
                return null;
              }
              if (key_0 === "maxAge") {
                const readyToMoveOptions = constructionStatusOptions.find(_temp4)?.options || [];
                const option = readyToMoveOptions.find(opt_1 => opt_1.value === value_0);
                label = `${labelKey}: ${option?.label || value_0}`;
              } else {
                if (key_0 === "possession") {
                  const underConstructionOptions = constructionStatusOptions.find(_temp5)?.options || [];
                  const option_0 = underConstructionOptions.find(opt_2 => opt_2.value === value_0);
                  label = `${labelKey}: ${option_0?.label || value_0}`;
                } else {
                  label = `${labelKey}: ${options.length ? getNameFromCode(options, value_0) || value_0 : value_0}`;
                }
              }
            } else {
              if (typeof value_0 === "number") {
                label = `${labelKey}: ${value_0}`;
              } else {
                label = `${labelKey}: ${value_0}`;
              }
            }
          }
        }
        return <div key={key_0} className="flex items-center bg-primary-100 text-primary-800 rounded-full pl-3 pr-1 py-1 text-sm border border-primary-200 shadow-sm" onClick={_temp6}><span className="font-medium mr-1">{label}</span><button type="button" aria-label={`Remove ${label} filter`} className="h-6 w-6 ml-1 text-primary-700 hover:text-primary-900 hover:bg-primary-200 rounded-full flex items-center justify-center transition-colors" onClick={e_0 => {
            onClearFilter(key_0 as keyof Filters);
            e_0.preventDefault();
            e_0.stopPropagation();
          }} onMouseDown={_temp7}><X className="h-4 w-4" /></button></div>;
      });
    }
    $[1] = filters;
    $[2] = onClearFilter;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
    t6 = $[7];
    t7 = $[8];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[10] !== t2 || $[11] !== t3) {
    t8 = <div className={t2}>{t3}</div>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t4 || $[14] !== t5 || $[15] !== t6 || $[16] !== t8) {
    t9 = <div className={t4} id={t5}>{t6}{t8}</div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
}

// PropertyFiltersData is now deprecated - use Filters from @/types/filters instead
function _temp7(e_1) {
  e_1.stopPropagation();
}
function _temp6(e) {
  return e.stopPropagation();
}
function _temp5(s_0) {
  return s_0.value === "underConstruction";
}
function _temp4(s) {
  return s.value === "readyToMoveIn";
}
function _temp3(val) {
  if (val >= 10000000) {
    return `${(val / 10000000).toFixed(1)}Cr`;
  } else {
    if (val >= 100000) {
      return `${(val / 100000).toFixed(1)}L`;
    }
  }
  return val.toString();
}
function _temp2(opt_0) {
  return {
    code: opt_0.value,
    name: opt_0.label
  };
}
function _temp(opt) {
  return {
    code: opt.value,
    name: opt.label
  };
}
interface PropertyFiltersProps {
  onApplyFilters?: (data: Partial<Filters>) => void;
  onClearFilters?: (data: Partial<Filters>) => void;
  onClose?: () => void;
  onClear?: () => void;
  defaultValues?: Partial<Filters>;
  className?: string;
  isMobile?: boolean;
  onResetForm?: (resetFn: () => void) => void;
}
export interface PropertyFiltersRef {
  clearFilters: () => void;
}
interface FilterSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  className?: string;
  disabled?: boolean;
  disabledReason?: string;
  allowOverflow?: boolean;
}
export function FilterSection(t0) {
  const $ = _c(32);
  const {
    title,
    children,
    defaultOpen: t1,
    className,
    disabled: t2,
    disabledReason,
    allowOverflow: t3
  } = t0;
  const defaultOpen = t1 === undefined ? true : t1;
  const disabled = t2 === undefined ? false : t2;
  const allowOverflow = t3 === undefined ? false : t3;
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const t4 = !allowOverflow && "overflow-hidden";
  let t5;
  if ($[0] !== className || $[1] !== t4) {
    t5 = cn("bg-white rounded-lg border border-gray-200", t4, className);
    $[0] = className;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== isOpen) {
    t6 = () => setIsOpen(!isOpen);
    $[3] = isOpen;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const t7 = disabled && "opacity-50 cursor-not-allowed";
  let t8;
  if ($[5] !== t7) {
    t8 = cn("w-full px-4 py-4 flex items-center justify-between text-left font-medium text-gray-900 hover:bg-gray-50 transition-colors", t7);
    $[5] = t7;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== title) {
    t9 = <span className="text-sm font-semibold">{title}</span>;
    $[7] = title;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== disabled || $[10] !== disabledReason) {
    t10 = disabled && disabledReason && <span className="text-xs text-gray-500 mt-1">{disabledReason}</span>;
    $[9] = disabled;
    $[10] = disabledReason;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t10 || $[13] !== t9) {
    t11 = <div className="flex flex-col">{t9}{t10}</div>;
    $[12] = t10;
    $[13] = t9;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== disabled || $[16] !== isOpen) {
    t12 = !disabled && (isOpen ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />);
    $[15] = disabled;
    $[16] = isOpen;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== disabled || $[19] !== t11 || $[20] !== t12 || $[21] !== t6 || $[22] !== t8) {
    t13 = <button type="button" onClick={t6} disabled={disabled} className={t8}>{t11}{t12}</button>;
    $[18] = disabled;
    $[19] = t11;
    $[20] = t12;
    $[21] = t6;
    $[22] = t8;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== children || $[25] !== disabled || $[26] !== isOpen) {
    t14 = isOpen && !disabled && <div className="px-4 pb-6">{children}</div>;
    $[24] = children;
    $[25] = disabled;
    $[26] = isOpen;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t13 || $[29] !== t14 || $[30] !== t5) {
    t15 = <div className={t5}>{t13}{t14}</div>;
    $[28] = t13;
    $[29] = t14;
    $[30] = t5;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  return t15;
}

// Use shared createFormDefaults function
const createFormDefaults = sharedCreateFormDefaults;
export function DesktopFilters(t0) {
  const $ = _c(320);
  const {
    onApplyFilters,
    onClose,
    defaultValues,
    className,
    isMobile,
    onResetForm
  } = t0;
  const {
    filters: urlFilters
  } = useUrlFilters();
  let t1;
  if ($[0] !== defaultValues || $[1] !== urlFilters) {
    t1 = () => ({
      ...createFormDefaults(),
      ...defaultValues,
      ...urlFilters
    });
    $[0] = defaultValues;
    $[1] = urlFilters;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [initialFormValues] = useState(t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const manualDirtyFields = useRef(t2);
  const [resetSignal, setResetSignal] = useState(false);
  const [shouldFocusLocation, setShouldFocusLocation] = useState(true);
  let t3;
  if ($[4] !== initialFormValues) {
    t3 = {
      defaultValues: initialFormValues,
      mode: "onChange",
      shouldFocusError: false
    };
    $[4] = initialFormValues;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const form = useForm(t3);
  const {
    control,
    handleSubmit,
    setValue,
    reset
  } = form;
  let t4;
  let t5;
  if ($[6] !== shouldFocusLocation) {
    t4 = () => {
      if (shouldFocusLocation) {
        const timer = setTimeout(() => {
          setShouldFocusLocation(false);
        }, 100);
        return () => clearTimeout(timer);
      }
    };
    t5 = [shouldFocusLocation];
    $[6] = shouldFocusLocation;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[9] !== onResetForm || $[10] !== reset) {
    t6 = () => {
      if (onResetForm) {
        onResetForm(() => {
          setResetSignal(true);
          const formDefaults = createFormDefaults();
          reset(formDefaults);
          manualDirtyFields.current = {};
        });
      }
    };
    t7 = [onResetForm, reset];
    $[9] = onResetForm;
    $[10] = reset;
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  let t8;
  if ($[13] !== form || $[14] !== onApplyFilters) {
    t8 = {
      form,
      manualDirtyFields,
      onApplyFilters
    };
    $[13] = form;
    $[14] = onApplyFilters;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const {
    handleApplyFilters
  } = useFilterHandlers(t8);
  let t9;
  if ($[16] !== control) {
    t9 = {
      control,
      name: "propertyType"
    };
    $[16] = control;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const watchedPropertyType = useWatch(t9);
  const watchedStatus = useWatch({
    control,
    name: "status"
  }) || "";
  const watchedBedrooms = useWatch({
    control,
    name: "bedrooms"
  }) || {
    min: 0,
    max: 0
  };
  let t10;
  if ($[18] !== control) {
    t10 = {
      control,
      name: "bathroomsMin"
    };
    $[18] = control;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const watchedBathroomsMin = useWatch(t10);
  let t11;
  if ($[20] !== control) {
    t11 = {
      control,
      name: "budget"
    };
    $[20] = control;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const watchedBudget = useWatch(t11);
  let t12;
  if ($[22] !== control) {
    t12 = {
      control,
      name: "documents"
    };
    $[22] = control;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const watchedDocuments = useWatch(t12);
  let t13;
  if ($[24] !== control) {
    t13 = {
      control,
      name: "facing"
    };
    $[24] = control;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const watchedFacing = useWatch(t13);
  let t14;
  if ($[26] !== control) {
    t14 = {
      control,
      name: "furnishing"
    };
    $[26] = control;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const watchedFurnishing = useWatch(t14);
  let t15;
  if ($[28] !== control) {
    t15 = {
      control,
      name: "rentalValue"
    };
    $[28] = control;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const watchedRentalValue = useWatch(t15);
  let t16;
  if ($[30] !== control) {
    t16 = {
      control,
      name: "spaces"
    };
    $[30] = control;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const watchedSpaces = useWatch(t16);
  let t17;
  if ($[32] !== control) {
    t17 = {
      control,
      name: "waterSources"
    };
    $[32] = control;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  const watchedWaterSources = useWatch(t17);
  let t18;
  if ($[34] !== control) {
    t18 = {
      control,
      name: "builtupAreaMin"
    };
    $[34] = control;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const watchedBuiltupAreaMin = useWatch(t18);
  let t19;
  if ($[36] !== control) {
    t19 = {
      control,
      name: "floors"
    };
    $[36] = control;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const watchedFloors = useWatch(t19);
  let t20;
  if ($[38] !== control) {
    t20 = {
      control,
      name: "balconiesMin"
    };
    $[38] = control;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  const watchedBalconiesMin = useWatch(t20);
  let t21;
  if ($[40] !== control) {
    t21 = {
      control,
      name: "parkingsMin"
    };
    $[40] = control;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  const watchedParkingsMin = useWatch(t21);
  let t22;
  if ($[42] !== control) {
    t22 = {
      control,
      name: "levelType"
    };
    $[42] = control;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  const watchedLevelType = useWatch(t22);
  let t23;
  if ($[44] !== control) {
    t23 = {
      control,
      name: "amenities"
    };
    $[44] = control;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  const watchedAmenities = useWatch(t23);
  let t24;
  if ($[46] !== control) {
    t24 = {
      control,
      name: "locations"
    };
    $[46] = control;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  const watchedLocations = useWatch(t24);
  let t25;
  if ($[48] !== control) {
    t25 = {
      control,
      name: "maxAge"
    };
    $[48] = control;
    $[49] = t25;
  } else {
    t25 = $[49];
  }
  const watchedMaxAge = useWatch(t25);
  let t26;
  if ($[50] !== control) {
    t26 = {
      control,
      name: "possession"
    };
    $[50] = control;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  const watchedPossession = useWatch(t26);
  let t27;
  if ($[52] !== control) {
    t27 = {
      control,
      name: "plotFacing"
    };
    $[52] = control;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  const watchedPlotFacing = useWatch(t27);
  let t28;
  if ($[54] !== control) {
    t28 = {
      control,
      name: "plotAreaMin"
    };
    $[54] = control;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  const watchedPlotAreaMin = useWatch(t28);
  const plotAreaMin = watchedPlotAreaMin ?? 0;
  let t29;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = fieldName => {
      manualDirtyFields.current[fieldName] = true;
    };
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  const markFieldDirty = t29;
  const watchedBedroomsMin = watchedBedrooms.min ?? 0;
  const watchedValues = {
    propertyType: watchedPropertyType,
    status: watchedStatus,
    bedrooms: watchedBedrooms,
    bathroomsMin: watchedBathroomsMin,
    budget: watchedBudget,
    documents: watchedDocuments,
    facing: watchedFacing,
    furnishing: watchedFurnishing,
    rentalValue: watchedRentalValue,
    spaces: watchedSpaces,
    waterSources: watchedWaterSources,
    builtupAreaMin: watchedBuiltupAreaMin,
    floors: watchedFloors,
    balconiesMin: watchedBalconiesMin,
    parkingsMin: watchedParkingsMin,
    levelType: watchedLevelType,
    amenities: watchedAmenities,
    locations: watchedLocations,
    maxAge: watchedMaxAge,
    possession: watchedPossession,
    ...(watchedPropertyType !== "apartment" && {
      plotFacing: watchedPlotFacing
    }),
    ...(watchedPropertyType !== "apartment" && {
      plotAreaMin
    })
  };
  const getLength = _temp8;
  const getMin = _temp9;
  const getMax = _temp10;
  const [activeSection, setActiveSection] = useState("search");
  const scrollContainerRef = useRef(null);
  let t30;
  if ($[57] !== setValue) {
    t30 = filterKey => {
      const formDefaults_0 = createFormDefaults();
      const defaultValue = formDefaults_0[filterKey];
      manualDirtyFields.current[filterKey] = true;
      setValue(filterKey as keyof Filters, defaultValue, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true
      });
    };
    $[57] = setValue;
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  const handleClearFilter = t30;
  useEffect(() => {
    const minAllowedBaths = watchedBedroomsMin > 1 ? Math.floor(watchedBedroomsMin) - 1 : 1;
    const currentBaths = watchedValues.bathroomsMin ?? 0;
    if (currentBaths > 0 && currentBaths < minAllowedBaths) {
      setValue("bathroomsMin", minAllowedBaths, {
        shouldDirty: true
      });
    }
  }, [watchedBedroomsMin, watchedValues.bathroomsMin, setValue]);
  let t31;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = [];
    $[59] = t31;
  } else {
    t31 = $[59];
  }
  useEffect(_temp12, t31);
  const isPlot = watchedPropertyType === "plot";
  const isFacingDisabled = isPlot;
  let t32;
  if ($[60] !== watchedPropertyType) {
    t32 = watchedPropertyType ? isPlotFacingDisabled(watchedPropertyType) : false;
    $[60] = watchedPropertyType;
    $[61] = t32;
  } else {
    t32 = $[61];
  }
  const plotFacingDisabled = t32;
  let t33;
  let t34;
  if ($[62] !== setValue || $[63] !== watchedPlotAreaMin || $[64] !== watchedPropertyType) {
    t33 = () => {
      handlePropertyTypeChange({
        propertyType: watchedPropertyType,
        plotAreaMin: watchedPlotAreaMin,
        setValue
      });
    };
    t34 = [watchedPropertyType, watchedPlotAreaMin, setValue];
    $[62] = setValue;
    $[63] = watchedPlotAreaMin;
    $[64] = watchedPropertyType;
    $[65] = t33;
    $[66] = t34;
  } else {
    t33 = $[65];
    t34 = $[66];
  }
  useEffect(t33, t34);
  let t35;
  let t36;
  if ($[67] !== setValue || $[68] !== watchedStatus) {
    t35 = () => {
      handleStatusChange(watchedStatus, setValue);
    };
    t36 = [watchedStatus, setValue];
    $[67] = setValue;
    $[68] = watchedStatus;
    $[69] = t35;
    $[70] = t36;
  } else {
    t35 = $[69];
    t36 = $[70];
  }
  useEffect(t35, t36);
  let t37;
  if ($[71] !== watchedStatus) {
    t37 = isMaxAgeDisabled(watchedStatus);
    $[71] = watchedStatus;
    $[72] = t37;
  } else {
    t37 = $[72];
  }
  const maxAgeDisabled = t37;
  let t38;
  if ($[73] !== watchedStatus) {
    t38 = isPossessionDisabled(watchedStatus);
    $[73] = watchedStatus;
    $[74] = t38;
  } else {
    t38 = $[74];
  }
  const possessionDisabled = t38;
  const minAllowedBathrooms = watchedBedroomsMin > 1 ? Math.floor(watchedBedroomsMin) - 1 : 1;
  const amenitiesLength = getLength(watchedValues.amenities);
  const balconiesMin = watchedValues.balconiesMin ?? 0;
  const bathroomsMin = watchedValues.bathroomsMin ?? 0;
  const bedroomsMax = getMax(watchedValues.bedrooms);
  const bedroomsMin = getMin(watchedValues.bedrooms);
  const budgetMax = watchedValues.budget?.max ?? 0;
  const budgetMin = watchedValues.budget?.min ?? 0;
  const builtupAreaMin = watchedValues.builtupAreaMin ?? 0;
  const documentsLength = getLength(watchedValues.documents);
  const facingLength = getLength(watchedValues.facing);
  const floorsMax = watchedValues.floors?.max ?? 0;
  const floorsMin = watchedValues.floors?.min ?? 0;
  const furnishing = watchedValues.furnishing;
  const levelTypeLength = getLength(watchedValues.levelType);
  const locationsLength = getLength(watchedValues.locations);
  const parkingsMin = watchedValues.parkingsMin ?? 0;
  const propertyType = watchedValues.propertyType;
  const rentalValueMax = watchedValues.rentalValue?.max ?? 0;
  const rentalValueMin = watchedValues.rentalValue?.min ?? 0;
  const spacesLength = getLength(watchedValues.spaces);
  const statusLength = getLength(watchedValues.status);
  const waterSourcesLength = getLength(watchedValues.waterSources);
  const t39 = locationsLength > 0;
  let t40;
  if ($[75] !== t39) {
    t40 = {
      id: "search",
      title: "Search",
      fullTitle: "Search locality / projects",
      hasValue: t39,
      icon: Search
    };
    $[75] = t39;
    $[76] = t40;
  } else {
    t40 = $[76];
  }
  const t41 = propertyType !== "";
  let t42;
  if ($[77] !== t41) {
    t42 = {
      id: "propertyType",
      title: "Property type",
      hasValue: t41,
      icon: Building
    };
    $[77] = t41;
    $[78] = t42;
  } else {
    t42 = $[78];
  }
  const t43 = bedroomsMin > 0 || bedroomsMax > 0;
  let t44;
  if ($[79] !== t43) {
    t44 = {
      id: "bedrooms",
      title: "Bedrooms",
      hasValue: t43,
      icon: BedDouble
    };
    $[79] = t43;
    $[80] = t44;
  } else {
    t44 = $[80];
  }
  const t45 = budgetMin > 0 || budgetMax > 0;
  let t46;
  if ($[81] !== t45) {
    t46 = {
      id: "budget",
      title: "Budget",
      hasValue: t45,
      icon: Wallet
    };
    $[81] = t45;
    $[82] = t46;
  } else {
    t46 = $[82];
  }
  const t47 = builtupAreaMin > 0;
  let t48;
  if ($[83] !== t47) {
    t48 = {
      id: "builtupArea",
      title: "Builtup area",
      fullTitle: "Builtup area (min.)",
      hasValue: t47,
      icon: Ruler
    };
    $[83] = t47;
    $[84] = t48;
  } else {
    t48 = $[84];
  }
  let t49;
  if ($[85] !== plotAreaMin || $[86] !== watchedPropertyType) {
    t49 = watchedPropertyType !== "apartment" ? [{
      id: "plotArea",
      title: "Plot area",
      fullTitle: "Plot area (min.)",
      hasValue: plotAreaMin > 0,
      icon: Maximize
    }] : [];
    $[85] = plotAreaMin;
    $[86] = watchedPropertyType;
    $[87] = t49;
  } else {
    t49 = $[87];
  }
  let t50;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t50 = {
      id: "divider",
      title: "divider",
      hasValue: false,
      icon: Search
    };
    $[88] = t50;
  } else {
    t50 = $[88];
  }
  const t51 = amenitiesLength > 0;
  let t52;
  if ($[89] !== t51) {
    t52 = {
      id: "amenities",
      title: "Amenities",
      hasValue: t51,
      icon: Sparkles
    };
    $[89] = t51;
    $[90] = t52;
  } else {
    t52 = $[90];
  }
  const t53 = balconiesMin > 0;
  let t54;
  if ($[91] !== t53) {
    t54 = {
      id: "balconies",
      title: "Balconies",
      fullTitle: "Balconies (min.)",
      hasValue: t53,
      icon: Grid3x3
    };
    $[91] = t53;
    $[92] = t54;
  } else {
    t54 = $[92];
  }
  const t55 = bathroomsMin > 0;
  let t56;
  if ($[93] !== t55) {
    t56 = {
      id: "bathrooms",
      title: "Bathrooms",
      fullTitle: "Bathrooms (min.)",
      hasValue: t55,
      icon: Bath
    };
    $[93] = t55;
    $[94] = t56;
  } else {
    t56 = $[94];
  }
  const t57 = documentsLength > 0;
  let t58;
  if ($[95] !== t57) {
    t58 = {
      id: "documents",
      title: "Documents",
      hasValue: t57,
      icon: FileText
    };
    $[95] = t57;
    $[96] = t58;
  } else {
    t58 = $[96];
  }
  const t59 = facingLength > 0;
  let t60;
  if ($[97] !== isFacingDisabled || $[98] !== t59) {
    t60 = {
      id: "facing",
      title: "Facing",
      hasValue: t59,
      disabled: isFacingDisabled,
      icon: Compass
    };
    $[97] = isFacingDisabled;
    $[98] = t59;
    $[99] = t60;
  } else {
    t60 = $[99];
  }
  const t61 = floorsMin > 0 || floorsMax > 0;
  let t62;
  if ($[100] !== t61) {
    t62 = {
      id: "floors",
      title: "Floors",
      hasValue: t61,
      icon: Layers
    };
    $[100] = t61;
    $[101] = t62;
  } else {
    t62 = $[101];
  }
  const t63 = Boolean(furnishing);
  let t64;
  if ($[102] !== t63) {
    t64 = {
      id: "furnishing",
      title: "Furnishing",
      hasValue: t63,
      icon: Sofa
    };
    $[102] = t63;
    $[103] = t64;
  } else {
    t64 = $[103];
  }
  const t65 = levelTypeLength > 0;
  let t66;
  if ($[104] !== t65) {
    t66 = {
      id: "levelType",
      title: "Level type",
      hasValue: t65,
      icon: ArrowUpDown
    };
    $[104] = t65;
    $[105] = t66;
  } else {
    t66 = $[105];
  }
  const t67 = watchedMaxAge !== "";
  let t68;
  if ($[106] !== maxAgeDisabled || $[107] !== t67) {
    t68 = {
      id: "maxAge",
      title: "Max age",
      hasValue: t67,
      disabled: maxAgeDisabled,
      icon: Clock
    };
    $[106] = maxAgeDisabled;
    $[107] = t67;
    $[108] = t68;
  } else {
    t68 = $[108];
  }
  const t69 = parkingsMin > 0;
  let t70;
  if ($[109] !== t69) {
    t70 = {
      id: "parkings",
      title: "Parkings",
      fullTitle: "Parkings (min.)",
      hasValue: t69,
      icon: Car
    };
    $[109] = t69;
    $[110] = t70;
  } else {
    t70 = $[110];
  }
  const t71 = getLength(watchedValues.plotFacing) > 0;
  let t72;
  if ($[111] !== plotFacingDisabled || $[112] !== t71) {
    t72 = {
      id: "plotFacing",
      title: "Plot Facing",
      hasValue: t71,
      disabled: plotFacingDisabled,
      icon: Compass
    };
    $[111] = plotFacingDisabled;
    $[112] = t71;
    $[113] = t72;
  } else {
    t72 = $[113];
  }
  const t73 = watchedPossession !== "";
  let t74;
  if ($[114] !== possessionDisabled || $[115] !== t73) {
    t74 = {
      id: "possession",
      title: "Possession",
      hasValue: t73,
      disabled: possessionDisabled,
      icon: Key
    };
    $[114] = possessionDisabled;
    $[115] = t73;
    $[116] = t74;
  } else {
    t74 = $[116];
  }
  const t75 = rentalValueMin > 0 || rentalValueMax > 0;
  let t76;
  if ($[117] !== t75) {
    t76 = {
      id: "rentalValue",
      title: "Rental value",
      hasValue: t75,
      icon: Banknote
    };
    $[117] = t75;
    $[118] = t76;
  } else {
    t76 = $[118];
  }
  const t77 = spacesLength > 0;
  let t78;
  if ($[119] !== t77) {
    t78 = {
      id: "spaces",
      title: "Spaces",
      hasValue: t77,
      icon: LayoutGrid
    };
    $[119] = t77;
    $[120] = t78;
  } else {
    t78 = $[120];
  }
  const t79 = statusLength > 0;
  let t80;
  if ($[121] !== t79) {
    t80 = {
      id: "status",
      title: "Status",
      hasValue: t79,
      icon: Hammer
    };
    $[121] = t79;
    $[122] = t80;
  } else {
    t80 = $[122];
  }
  const t81 = waterSourcesLength > 0;
  let t82;
  if ($[123] !== t81) {
    t82 = {
      id: "waterSources",
      title: "Water sources",
      hasValue: t81,
      icon: Droplets
    };
    $[123] = t81;
    $[124] = t82;
  } else {
    t82 = $[124];
  }
  let t83;
  if ($[125] !== t40 || $[126] !== t42 || $[127] !== t44 || $[128] !== t46 || $[129] !== t48 || $[130] !== t49 || $[131] !== t52 || $[132] !== t54 || $[133] !== t56 || $[134] !== t58 || $[135] !== t60 || $[136] !== t62 || $[137] !== t64 || $[138] !== t66 || $[139] !== t68 || $[140] !== t70 || $[141] !== t72 || $[142] !== t74 || $[143] !== t76 || $[144] !== t78 || $[145] !== t80 || $[146] !== t82) {
    t83 = [t40, t42, t44, t46, t48, ...t49, t50, t52, t54, t56, t58, t60, t62, t64, t66, t68, t70, t72, t74, t76, t78, t80, t82];
    $[125] = t40;
    $[126] = t42;
    $[127] = t44;
    $[128] = t46;
    $[129] = t48;
    $[130] = t49;
    $[131] = t52;
    $[132] = t54;
    $[133] = t56;
    $[134] = t58;
    $[135] = t60;
    $[136] = t62;
    $[137] = t64;
    $[138] = t66;
    $[139] = t68;
    $[140] = t70;
    $[141] = t72;
    $[142] = t74;
    $[143] = t76;
    $[144] = t78;
    $[145] = t80;
    $[146] = t82;
    $[147] = t83;
  } else {
    t83 = $[147];
  }
  const sections = t83;
  const filterSections = sections;
  const scrollToSection = sectionId => {
    const element = document.getElementById(`filter-${sectionId}`);
    if (element && scrollContainerRef.current) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
      setActiveSection(sectionId);
    }
  };
  let t84;
  if ($[148] !== filterSections || $[149] !== setActiveSection) {
    t84 = () => {
      const container = scrollContainerRef.current;
      if (!container) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        const containerTop = container.getBoundingClientRect().top;
        const intersectingEntries = entries.filter(_temp13);
        if (intersectingEntries.length === 0) {
          return;
        }
        intersectingEntries.sort((a, b) => Math.abs(a.boundingClientRect.top - containerTop) - Math.abs(b.boundingClientRect.top - containerTop));
        if (intersectingEntries.length > 0) {
          const sectionId_0 = intersectingEntries[0].target.id.replace("filter-", "");
          setActiveSection(sectionId_0);
        }
      }, {
        root: container,
        threshold: 0.1,
        rootMargin: "0px 0px -80% 0px"
      });
      filterSections.forEach(section => {
        const element_0 = document.getElementById(`filter-${section.id}`);
        if (element_0) {
          observer.observe(element_0);
        }
      });
      return () => observer.disconnect();
    };
    $[148] = filterSections;
    $[149] = setActiveSection;
    $[150] = t84;
  } else {
    t84 = $[150];
  }
  let t85;
  if ($[151] !== filterSections) {
    t85 = [filterSections];
    $[151] = filterSections;
    $[152] = t85;
  } else {
    t85 = $[152];
  }
  useEffect(t84, t85);
  const T0 = MapProvider;
  let t86;
  if ($[153] !== className) {
    t86 = cn("w-full max-w-md md:max-w-none bg-gray-50 flex flex-col", className);
    $[153] = className;
    $[154] = t86;
  } else {
    t86 = $[154];
  }
  let t87;
  if ($[155] !== isMobile) {
    t87 = isMobile && <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4" />;
    $[155] = isMobile;
    $[156] = t87;
  } else {
    t87 = $[156];
  }
  let t88;
  if ($[157] === Symbol.for("react.memo_cache_sentinel")) {
    t88 = <h2 className="text-xl font-bold text-gray-800">Filters</h2>;
    $[157] = t88;
  } else {
    t88 = $[157];
  }
  let t89;
  if ($[158] === Symbol.for("react.memo_cache_sentinel")) {
    t89 = <X className="h-5 w-5" />;
    $[158] = t89;
  } else {
    t89 = $[158];
  }
  let t90;
  if ($[159] !== onClose) {
    t90 = <div className="flex items-center justify-between">{t88}<Button variant="ghost" size="icon" className="h-8 w-8 text-gray-600 hover:text-gray-800 hover:bg-gray-100" aria-label="Close filters" onPress={onClose}>{t89}</Button></div>;
    $[159] = onClose;
    $[160] = t90;
  } else {
    t90 = $[160];
  }
  let t91;
  if ($[161] !== t87 || $[162] !== t90) {
    t91 = <div className="flex-shrink-0 p-4 border-b">{t87}{t90}</div>;
    $[161] = t87;
    $[162] = t90;
    $[163] = t91;
  } else {
    t91 = $[163];
  }
  const t92 = "flex-1 flex min-h-0";
  const t93 = "w-64 flex-shrink-0 bg-white border-r border-gray-200 overflow-y-auto overflow-x-hidden sticky top-0 self-start custom-scrollbar h-full";
  const t94 = "p-3";
  const t95 = "space-y-1";
  const t96 = filterSections.map(section_0 => section_0.id === "divider" ? <div key={section_0.id} className="border-t border-gray-200 my-2" /> : <button type="button" aria-label={`Scroll to ${section_0.title}`} key={section_0.id} onClick={() => scrollToSection(section_0.id)} disabled={section_0.disabled} className={cn("w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 relative group flex items-center overflow-hidden", activeSection === section_0.id ? "bg-primary-50 text-primary-700 shadow-sm ring-1 ring-primary-100" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900", section_0.disabled && "opacity-50 cursor-not-allowed hover:bg-transparent")}><div className="flex items-center flex-1 min-w-0 pr-4">{section_0.icon && <section_0.icon className={cn("w-4 h-4 mr-3 flex-shrink-0 transition-colors", activeSection === section_0.id ? "text-primary-600" : "text-gray-400 group-hover:text-gray-600")} />}<span className="truncate">{section_0.title}</span></div>{section_0.hasValue && <div className="absolute right-2 top-1/2 -translate-y-1/2 w-2 h-2 bg-red-500 rounded-full shadow-sm ring-2 ring-white ml-2 flex-shrink-0" />}</button>);
  let t97;
  if ($[164] !== t96) {
    t97 = <div className={t93}><div className={t94}><nav className={t95}>{t96}</nav></div></div>;
    $[164] = t96;
    $[165] = t97;
  } else {
    t97 = $[165];
  }
  let t98;
  if ($[166] !== handleApplyFilters || $[167] !== handleSubmit) {
    t98 = handleSubmit(handleApplyFilters);
    $[166] = handleApplyFilters;
    $[167] = handleSubmit;
    $[168] = t98;
  } else {
    t98 = $[168];
  }
  const t99 = watchedValues as Partial<Filters>;
  let t100;
  if ($[169] !== handleClearFilter || $[170] !== t99) {
    t100 = <SelectedFilters filters={t99} onClearFilter={handleClearFilter} />;
    $[169] = handleClearFilter;
    $[170] = t99;
    $[171] = t100;
  } else {
    t100 = $[171];
  }
  let t101;
  if ($[172] !== markFieldDirty || $[173] !== shouldFocusLocation) {
    t101 = t102 => {
      const {
        field
      } = t102;
      return <PlaceAutocompleteWrapper className={cn("w-full px-4 pb-2 border border-gray-300 rounded-lg", "focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500", "disabled:bg-gray-50", "placeholder-gray-400 text-sm", "disabled:cursor-not-allowed disabled:opacity-50 md:text-sm [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none")} value={field.value || []} onChange={value => {
        field.onChange(value);
        markFieldDirty("locations");
      }} showChips={true} autoFocus={shouldFocusLocation} filterField={true} />;
    };
    $[172] = markFieldDirty;
    $[173] = shouldFocusLocation;
    $[174] = t101;
  } else {
    t101 = $[174];
  }
  let t102;
  if ($[175] !== control || $[176] !== t101) {
    t102 = <div id="filter-search"><FilterSection title="Search locality / projects" allowOverflow={true}><Controller name="locations" control={control} render={t101} /></FilterSection></div>;
    $[175] = control;
    $[176] = t101;
    $[177] = t102;
  } else {
    t102 = $[177];
  }
  let t103;
  if ($[178] !== control || $[179] !== markFieldDirty || $[180] !== setValue) {
    t103 = <div id="filter-propertyType"><FilterSection title="Property type"><PropertyTypeField options={propertyTypeOptions} control={control} exactPropertyTypeName="subType" setValue={setValue} exactTypeRequired={true} hideLabel={true} markFieldDirty={markFieldDirty} /></FilterSection></div>;
    $[178] = control;
    $[179] = markFieldDirty;
    $[180] = setValue;
    $[181] = t103;
  } else {
    t103 = $[181];
  }
  let t104;
  if ($[182] !== markFieldDirty) {
    t104 = t105 => {
      const {
        field: field_0
      } = t105;
      return <BedroomsSection value={field_0.value} onChange={value_0 => {
        field_0.onChange(value_0);
        markFieldDirty("bedrooms");
      }} />;
    };
    $[182] = markFieldDirty;
    $[183] = t104;
  } else {
    t104 = $[183];
  }
  let t105;
  if ($[184] !== control || $[185] !== t104) {
    t105 = <div id="filter-bedrooms"><FilterSection title="Bedrooms"><Controller name="bedrooms" control={control} render={t104} /></FilterSection></div>;
    $[184] = control;
    $[185] = t104;
    $[186] = t105;
  } else {
    t105 = $[186];
  }
  let t106;
  if ($[187] !== control || $[188] !== markFieldDirty || $[189] !== resetSignal) {
    t106 = <div id="filter-budget"><FilterSection title="Budget"><BudgetNew name="budget" control={control} markFieldDirty={markFieldDirty} resetSignal={resetSignal} /></FilterSection></div>;
    $[187] = control;
    $[188] = markFieldDirty;
    $[189] = resetSignal;
    $[190] = t106;
  } else {
    t106 = $[190];
  }
  let t107;
  if ($[191] !== markFieldDirty) {
    t107 = t108 => {
      const {
        field: field_1
      } = t108;
      return <DynamicAreaSection {...field_1} onChange={value_1 => {
        field_1.onChange(value_1);
        markFieldDirty("builtupAreaMin");
      }} />;
    };
    $[191] = markFieldDirty;
    $[192] = t107;
  } else {
    t107 = $[192];
  }
  let t108;
  if ($[193] !== control || $[194] !== t107) {
    t108 = <div id="filter-builtupArea"><FilterSection title="Builtup area (min.)"><Controller name="builtupAreaMin" control={control} render={t107} /></FilterSection></div>;
    $[193] = control;
    $[194] = t107;
    $[195] = t108;
  } else {
    t108 = $[195];
  }
  let t109;
  if ($[196] !== control || $[197] !== markFieldDirty || $[198] !== watchedPropertyType) {
    t109 = watchedPropertyType !== "apartment" && <div id="filter-plotArea"><FilterSection title="Plot area (min.)"><Controller name="plotAreaMin" control={control} render={t110 => {
          const {
            field: field_2
          } = t110;
          return <DynamicAreaSection {...field_2} value={field_2.value ?? 0} onChange={value_2 => {
            field_2.onChange(value_2);
            markFieldDirty("plotAreaMin");
          }} />;
        }} /></FilterSection></div>;
    $[196] = control;
    $[197] = markFieldDirty;
    $[198] = watchedPropertyType;
    $[199] = t109;
  } else {
    t109 = $[199];
  }
  let t110;
  if ($[200] === Symbol.for("react.memo_cache_sentinel")) {
    t110 = <div id="filter-divider" className="border-t border-gray-300 my-8" />;
    $[200] = t110;
  } else {
    t110 = $[200];
  }
  let t111;
  if ($[201] !== control || $[202] !== markFieldDirty) {
    t111 = <div id="filter-amenities"><FilterSection title="Amenities"><AmenitiesIconGrid hideLabel={true} allowCustom={false} control={control} markFieldDirty={markFieldDirty} /></FilterSection></div>;
    $[201] = control;
    $[202] = markFieldDirty;
    $[203] = t111;
  } else {
    t111 = $[203];
  }
  let t112;
  if ($[204] !== markFieldDirty) {
    t112 = t113 => {
      const {
        field: field_3
      } = t113;
      return <BalconiesSection {...field_3} onChange={value_3 => {
        field_3.onChange(value_3);
        markFieldDirty("balconiesMin");
      }} />;
    };
    $[204] = markFieldDirty;
    $[205] = t112;
  } else {
    t112 = $[205];
  }
  let t113;
  if ($[206] !== control || $[207] !== t112) {
    t113 = <div id="filter-balconies"><FilterSection title="Balconies (min.)"><Controller name="balconiesMin" control={control} render={t112} /></FilterSection></div>;
    $[206] = control;
    $[207] = t112;
    $[208] = t113;
  } else {
    t113 = $[208];
  }
  let t114;
  if ($[209] !== markFieldDirty || $[210] !== minAllowedBathrooms) {
    t114 = t115 => {
      const {
        field: field_4
      } = t115;
      return <BathroomsSection {...field_4} onChange={value_4 => {
        field_4.onChange(value_4);
        markFieldDirty("bathroomsMin");
      }} minAllowed={minAllowedBathrooms} />;
    };
    $[209] = markFieldDirty;
    $[210] = minAllowedBathrooms;
    $[211] = t114;
  } else {
    t114 = $[211];
  }
  let t115;
  if ($[212] !== control || $[213] !== t114) {
    t115 = <div id="filter-bathrooms"><FilterSection title="Bathrooms (min.)"><Controller name="bathroomsMin" control={control} render={t114} /></FilterSection></div>;
    $[212] = control;
    $[213] = t114;
    $[214] = t115;
  } else {
    t115 = $[214];
  }
  let t116;
  if ($[215] === Symbol.for("react.memo_cache_sentinel")) {
    t116 = formikOptions(documentsOptions);
    $[215] = t116;
  } else {
    t116 = $[215];
  }
  let t117;
  if ($[216] !== control || $[217] !== markFieldDirty) {
    t117 = <div id="filter-documents"><FilterSection title="Documents"><Input control={control} name="documents" itemsClassName="grid grid-cols-2 gap-2" options={t116} type="checkbox" markFieldDirty={markFieldDirty} /></FilterSection></div>;
    $[216] = control;
    $[217] = markFieldDirty;
    $[218] = t117;
  } else {
    t117 = $[218];
  }
  let t118;
  if ($[219] === Symbol.for("react.memo_cache_sentinel")) {
    t118 = formikOptions(facingOptions);
    $[219] = t118;
  } else {
    t118 = $[219];
  }
  let t119;
  if ($[220] !== control || $[221] !== markFieldDirty) {
    t119 = <Input control={control} name="facing" itemsClassName="grid grid-cols-4 gap-2" options={t118} type="checkbox" markFieldDirty={markFieldDirty} />;
    $[220] = control;
    $[221] = markFieldDirty;
    $[222] = t119;
  } else {
    t119 = $[222];
  }
  let t120;
  if ($[223] !== isFacingDisabled || $[224] !== t119) {
    t120 = <div id="filter-facing"><FilterSection title="Facing" disabled={isFacingDisabled} disabledReason="Not applicable for Plot">{t119}</FilterSection></div>;
    $[223] = isFacingDisabled;
    $[224] = t119;
    $[225] = t120;
  } else {
    t120 = $[225];
  }
  let t121;
  if ($[226] !== markFieldDirty) {
    t121 = t122 => {
      const {
        field: field_5
      } = t122;
      return <FloorsSection value={field_5.value} onChange={value_5 => {
        field_5.onChange(value_5);
        markFieldDirty("floors");
      }} />;
    };
    $[226] = markFieldDirty;
    $[227] = t121;
  } else {
    t121 = $[227];
  }
  let t122;
  if ($[228] !== control || $[229] !== t121) {
    t122 = <div id="filter-floors"><FilterSection title="Floors"><Controller name="floors" control={control} render={t121} /></FilterSection></div>;
    $[228] = control;
    $[229] = t121;
    $[230] = t122;
  } else {
    t122 = $[230];
  }
  let t123;
  if ($[231] === Symbol.for("react.memo_cache_sentinel")) {
    t123 = formikOptions(furnishingOptions);
    $[231] = t123;
  } else {
    t123 = $[231];
  }
  let t124;
  if ($[232] !== control || $[233] !== markFieldDirty) {
    t124 = <div id="filter-furnishing"><FilterSection title="Furnishing"><Input control={control} name="furnishing" itemsClassName="grid grid-cols-2 gap-2" options={t123} type="radio" markFieldDirty={markFieldDirty} /></FilterSection></div>;
    $[232] = control;
    $[233] = markFieldDirty;
    $[234] = t124;
  } else {
    t124 = $[234];
  }
  let t125;
  if ($[235] === Symbol.for("react.memo_cache_sentinel")) {
    t125 = formikOptions(levelOptions);
    $[235] = t125;
  } else {
    t125 = $[235];
  }
  let t126;
  if ($[236] !== control || $[237] !== markFieldDirty) {
    t126 = <div id="filter-levelType"><FilterSection title="Level type"><Input control={control} name="levelType" itemsClassName="grid grid-cols-3 gap-2" options={t125} type="checkbox" markFieldDirty={markFieldDirty} /></FilterSection></div>;
    $[236] = control;
    $[237] = markFieldDirty;
    $[238] = t126;
  } else {
    t126 = $[238];
  }
  let t127;
  if ($[239] !== markFieldDirty) {
    t127 = t128 => {
      const {
        field: field_6
      } = t128;
      return <MaxAgeSection value={field_6.value} onChange={value_6 => {
        field_6.onChange(value_6);
        markFieldDirty("maxAge");
      }} />;
    };
    $[239] = markFieldDirty;
    $[240] = t127;
  } else {
    t127 = $[240];
  }
  let t128;
  if ($[241] !== control || $[242] !== t127) {
    t128 = <Controller name="maxAge" control={control} render={t127} />;
    $[241] = control;
    $[242] = t127;
    $[243] = t128;
  } else {
    t128 = $[243];
  }
  let t129;
  if ($[244] !== maxAgeDisabled || $[245] !== t128) {
    t129 = <div id="filter-maxAge"><FilterSection title="Max age" disabled={maxAgeDisabled} disabledReason="Applicable on status 'READY TO MOVE IN'">{t128}</FilterSection></div>;
    $[244] = maxAgeDisabled;
    $[245] = t128;
    $[246] = t129;
  } else {
    t129 = $[246];
  }
  let t130;
  if ($[247] !== markFieldDirty) {
    t130 = t131 => {
      const {
        field: field_7
      } = t131;
      return <ParkingsSection {...field_7} onChange={value_7 => {
        field_7.onChange(value_7);
        markFieldDirty("parkingsMin");
      }} />;
    };
    $[247] = markFieldDirty;
    $[248] = t130;
  } else {
    t130 = $[248];
  }
  let t131;
  if ($[249] !== control || $[250] !== t130) {
    t131 = <div id="filter-parkings"><FilterSection title="Parkings (min.)"><Controller name="parkingsMin" control={control} render={t130} /></FilterSection></div>;
    $[249] = control;
    $[250] = t130;
    $[251] = t131;
  } else {
    t131 = $[251];
  }
  let t132;
  if ($[252] === Symbol.for("react.memo_cache_sentinel")) {
    t132 = formikOptions(facingOptions);
    $[252] = t132;
  } else {
    t132 = $[252];
  }
  let t133;
  if ($[253] !== control || $[254] !== markFieldDirty || $[255] !== setValue) {
    t133 = <Input setValue={setValue} control={control} itemsClassName="grid grid-cols-4 gap-2" options={t132} name="plotFacing" type="checkbox" markFieldDirty={markFieldDirty} />;
    $[253] = control;
    $[254] = markFieldDirty;
    $[255] = setValue;
    $[256] = t133;
  } else {
    t133 = $[256];
  }
  let t134;
  if ($[257] !== plotFacingDisabled || $[258] !== t133) {
    t134 = <div id="filter-plotFacing"><FilterSection title="Plot Facing" disabled={plotFacingDisabled} disabledReason="Not applicable for Apartment">{t133}</FilterSection></div>;
    $[257] = plotFacingDisabled;
    $[258] = t133;
    $[259] = t134;
  } else {
    t134 = $[259];
  }
  let t135;
  if ($[260] !== markFieldDirty) {
    t135 = t136 => {
      const {
        field: field_8
      } = t136;
      return <PossessionSection value={field_8.value} onChange={value_8 => {
        field_8.onChange(value_8);
        markFieldDirty("possession");
      }} />;
    };
    $[260] = markFieldDirty;
    $[261] = t135;
  } else {
    t135 = $[261];
  }
  let t136;
  if ($[262] !== control || $[263] !== t135) {
    t136 = <Controller name="possession" control={control} render={t135} />;
    $[262] = control;
    $[263] = t135;
    $[264] = t136;
  } else {
    t136 = $[264];
  }
  let t137;
  if ($[265] !== possessionDisabled || $[266] !== t136) {
    t137 = <div id="filter-possession"><FilterSection title="Possession" disabled={possessionDisabled} disabledReason="Applicable on status 'UNDER CONSTRUCTION'">{t136}</FilterSection></div>;
    $[265] = possessionDisabled;
    $[266] = t136;
    $[267] = t137;
  } else {
    t137 = $[267];
  }
  let t138;
  if ($[268] !== markFieldDirty) {
    t138 = t139 => {
      const {
        field: field_9
      } = t139;
      return <RentalValueSection value={field_9.value} onChange={value_9 => {
        field_9.onChange(value_9);
        markFieldDirty("rentalValue");
      }} />;
    };
    $[268] = markFieldDirty;
    $[269] = t138;
  } else {
    t138 = $[269];
  }
  let t139;
  if ($[270] !== control || $[271] !== t138) {
    t139 = <div id="filter-rentalValue"><FilterSection title="Rental value"><Controller name="rentalValue" control={control} render={t138} /></FilterSection></div>;
    $[270] = control;
    $[271] = t138;
    $[272] = t139;
  } else {
    t139 = $[272];
  }
  let t140;
  if ($[273] === Symbol.for("react.memo_cache_sentinel")) {
    t140 = formikOptions(spacesOptions);
    $[273] = t140;
  } else {
    t140 = $[273];
  }
  let t141;
  if ($[274] !== control || $[275] !== markFieldDirty) {
    t141 = <div id="filter-spaces"><FilterSection title="Spaces"><Input control={control} name="spaces" itemsClassName="grid grid-cols-4 gap-2" options={t140} type="checkbox" markFieldDirty={markFieldDirty} /></FilterSection></div>;
    $[274] = control;
    $[275] = markFieldDirty;
    $[276] = t141;
  } else {
    t141 = $[276];
  }
  let t142;
  if ($[277] !== control || $[278] !== markFieldDirty) {
    t142 = <div id="filter-status"><FilterSection title="Status"><Input control={control} name="status" itemsClassName="grid grid-cols-3 gap-2" options={constructionStatusOptions} type="radio" markFieldDirty={markFieldDirty} /></FilterSection></div>;
    $[277] = control;
    $[278] = markFieldDirty;
    $[279] = t142;
  } else {
    t142 = $[279];
  }
  let t143;
  if ($[280] === Symbol.for("react.memo_cache_sentinel")) {
    t143 = formikOptions(waterSourcesShortOptions);
    $[280] = t143;
  } else {
    t143 = $[280];
  }
  let t144;
  if ($[281] !== control || $[282] !== markFieldDirty || $[283] !== setValue) {
    t144 = <div id="filter-waterSources"><FilterSection title="Water sources"><Input setValue={setValue} control={control} itemsClassName="grid grid-cols-3 gap-2" options={t143} name="waterSources" type="checkbox" markFieldDirty={markFieldDirty} /></FilterSection></div>;
    $[281] = control;
    $[282] = markFieldDirty;
    $[283] = setValue;
    $[284] = t144;
  } else {
    t144 = $[284];
  }
  let t145;
  if ($[285] !== t100 || $[286] !== t102 || $[287] !== t103 || $[288] !== t105 || $[289] !== t106 || $[290] !== t108 || $[291] !== t109 || $[292] !== t111 || $[293] !== t113 || $[294] !== t115 || $[295] !== t117 || $[296] !== t120 || $[297] !== t122 || $[298] !== t124 || $[299] !== t126 || $[300] !== t129 || $[301] !== t131 || $[302] !== t134 || $[303] !== t137 || $[304] !== t139 || $[305] !== t141 || $[306] !== t142 || $[307] !== t144 || $[308] !== t98) {
    t145 = <div className="flex-1 overflow-y-auto" ref={scrollContainerRef}><form id="property-filters-form" onSubmit={t98} className="p-6 space-y-8">{t100}{t102}{t103}{t105}{t106}{t108}{t109}{t110}{t111}{t113}{t115}{t117}{t120}{t122}{t124}{t126}{t129}{t131}{t134}{t137}{t139}{t141}{t142}{t144}</form></div>;
    $[285] = t100;
    $[286] = t102;
    $[287] = t103;
    $[288] = t105;
    $[289] = t106;
    $[290] = t108;
    $[291] = t109;
    $[292] = t111;
    $[293] = t113;
    $[294] = t115;
    $[295] = t117;
    $[296] = t120;
    $[297] = t122;
    $[298] = t124;
    $[299] = t126;
    $[300] = t129;
    $[301] = t131;
    $[302] = t134;
    $[303] = t137;
    $[304] = t139;
    $[305] = t141;
    $[306] = t142;
    $[307] = t144;
    $[308] = t98;
    $[309] = t145;
  } else {
    t145 = $[309];
  }
  let t146;
  if ($[310] !== t145 || $[311] !== t97) {
    t146 = <div className={t92}>{t97}{t145}</div>;
    $[310] = t145;
    $[311] = t97;
    $[312] = t146;
  } else {
    t146 = $[312];
  }
  let t147;
  if ($[313] !== t146 || $[314] !== t86 || $[315] !== t91) {
    t147 = <div className={t86}>{t91}{t146}</div>;
    $[313] = t146;
    $[314] = t86;
    $[315] = t91;
    $[316] = t147;
  } else {
    t147 = $[316];
  }
  let t148;
  if ($[317] !== T0 || $[318] !== t147) {
    t148 = <T0>{t147}</T0>;
    $[317] = T0;
    $[318] = t147;
    $[319] = t148;
  } else {
    t148 = $[319];
  }
  return t148;
}
function _temp13(e_0) {
  return e_0.isIntersecting;
}
function _temp12() {
  const handler = _temp11;
  document.addEventListener("mousedown", handler, true);
  return () => {
    document.removeEventListener("mousedown", handler, true);
  };
}
function _temp11(e) {
  const dropdown = window.settlinDropdownRef;
  if (dropdown && e.target instanceof Node && dropdown.contains(e.target)) {
    e.stopPropagation();
    e.preventDefault();
    e.stopImmediatePropagation();
    return false;
  }
}
function _temp10(obj_0) {
  return obj_0?.max ?? 0;
}
function _temp9(obj) {
  return obj?.min ?? 0;
}
function _temp8(arr) {
  return Array.isArray(arr) ? arr.length : 0;
}